'use client';

import { useState } from 'react';
import { EVAL_MAP_COUNT, type SimResult } from '@/lib/swarm/types';

interface AuthUser {
  githubUsername: string;
  avatarUrl: string | null;
}

interface SwarmBottomBarProps {
  onSubmit: () => void;
  onCancel: () => void;
  scoring: boolean;
  submitStatus: string | null;
  submitMapsCompleted: number | null;
  submitMapsTotal: number | null;
  scores: Map<number, SimResult>;
  scoresStale: boolean;
  totalMaps: number;
  user: AuthUser | null;
  authLoading: boolean;
  competitionClosed?: boolean;
}

export function SwarmBottomBar({
  onSubmit,
  onCancel,
  scoring,
  submitStatus,
  submitMapsCompleted,
  submitMapsTotal,
  scores,
  scoresStale,
  totalMaps,
  user,
  authLoading,
  competitionClosed,
}: SwarmBottomBarProps) {
  const ratioSum = Array.from(scores.values()).reduce((s, r) => s + (r.totalFood > 0 ? r.foodCollected / r.totalFood : 0), 0);
  const score = scores.size > 0 ? Math.round((ratioSum / scores.size) * 1000) : 0;
  const mapsScored = scores.size;

  return (
    <div
      className="flex items-center justify-between px-4 py-2 bg-panel-header border-t border-stroke text-[10px] tracking-wider shrink-0"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <SubmitButton onClick={onSubmit} disabled={scoring || authLoading} label={scoring ? 'Scoring...' : authLoading ? 'Loading...' : competitionClosed ? (user ? 'Submit (Free Play)' : 'Sign in to Submit') : user ? 'Submit' : 'Sign in to Submit'} />

        {submitStatus && (
          <>
            <span className="text-dim shrink-0">|</span>
            {scoring && submitMapsCompleted !== null && submitMapsTotal !== null && submitMapsTotal > 0 ? (
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-32 h-1.5 bg-stroke rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber rounded-full transition-all duration-500"
                    style={{ width: `${(submitMapsCompleted / submitMapsTotal) * 100}%` }}
                  />
                </div>
                <span className="text-secondary">{submitMapsCompleted}/{submitMapsTotal} maps</span>
                <button
                  onClick={onCancel}
                  className="text-dim hover:text-red cursor-pointer"
                >cancel</button>
              </div>
            ) : (
              <span className={`shrink-0 ${submitStatus.startsWith('Score:') ? 'text-amber font-bold' : submitStatus.startsWith('Error') || submitStatus.startsWith('Failed') ? 'text-red' : 'text-secondary'}`}>
                {submitStatus}
              </span>
            )}
          </>
        )}

      </div>

      <div className="flex items-center gap-4 shrink-0 ml-3">
        {mapsScored > 0 && (
          <span className={`font-bold ${scoresStale ? 'text-amber/40 animate-pulse' : 'text-amber'}`}>
            {scoresStale ? 'Scoring' : 'Score'}: {score}/1000
          </span>
        )}
      </div>
    </div>
  );
}

function SubmitButton({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  const [show, setShow] = useState(false);

  return (
    <div
      className="relative shrink-0"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <button
        onClick={onClick}
        disabled={disabled}
        className="px-3 py-1 rounded border border-amber/40 hover:border-amber hover:text-amber text-secondary transition-colors disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
      >
        {label}
      </button>
      {show && !disabled && (
        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-elevated border border-stroke rounded shadow-lg text-[10px] leading-relaxed whitespace-nowrap pointer-events-none z-50">
          <span className="text-primary">Evaluated server-side against {EVAL_MAP_COUNT} scored maps.</span>
          <br />
          <span className="text-dim">Only your best score appears on the leaderboard.</span>
          <div className="absolute top-full left-4 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-stroke" />
        </div>
      )}
    </div>
  );
}
